import { useEffect, useRef, type RefObject } from 'react';

// Standard keyboard behavior for a modal dialog (WAI-ARIA APG "Dialog (Modal)"):
// - on open, moves focus to `initialFocusRef` (or the first focusable element in the dialog);
// - Escape calls `onEscape`;
// - Tab / Shift+Tab wrap inside the dialog (focus can't leak to the page behind);
// - on close/unmount, focus returns to the element that had it before opening.
// Our modals render with DaisyUI classes (not `showModal()`), so the browser gives none of this.

// Open dialogs, innermost last. Only the top one handles keys, so a dialog opened from inside
// another (e.g. a confirm inside the pitch upload modal) gets Escape/Tab, not its parent —
// all listeners sit on `document` and the parent's was registered first.
const openDialogs: symbol[] = [];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UseDialogFocusOptions {
  isOpen: boolean;
  containerRef: RefObject<HTMLElement | null>;
  onEscape: () => void;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

export function useDialogFocus({
  isOpen,
  containerRef,
  onEscape,
  initialFocusRef,
}: UseDialogFocusOptions) {
  // Latest-callback ref: callers pass inline handlers, and re-running the effect on every
  // render would steal focus back to the initial element.
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!isOpen) return;

    const token = Symbol('dialog');
    openDialogs.push(token);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);

    (initialFocusRef?.current ?? focusables()[0])?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || openDialogs[openDialogs.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !containerRef.current?.contains(active);

      if (e.shiftKey && (active === first || outside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || outside)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      openDialogs.splice(openDialogs.indexOf(token), 1);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, containerRef, initialFocusRef]);
}
